import { useMemo, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { CalendarX2, RefreshCw, CloudCog } from 'lucide-react'
import { startOfWeek } from 'date-fns'
import { apiClient } from '@/lib/api.ts'
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  Input,
  LoadingState,
  PageHeader,
  useToast,
} from '@/components/ui'
import type { ColumnDef } from '@tanstack/react-table'
import type { DispoCalendarEmployee, DispoCalendarEvent } from '@shared/types'
import { DispositionCalendar } from './components/DispositionCalendar/DispositionCalendar.tsx'

// ─── Types ────────────────────────────────────────────────────────────────────

type AbsenceTypeFilter = string | 'all'

interface AbwesenheitResponse {
  dataSource: 'tanss' | 'none'
  employees: DispoCalendarEmployee[]
  window: { from: string; to: string } | null
  error?: string
}

interface SyncResult {
  ok: boolean
  created: number
  updated: number
  deleted: number
  errors: number
  errorMessages: string[]
}

// Normalized view of a single absence for the DataTable
interface AbsenceRow {
  id: string
  employeeId: number
  employeeName: string
  planningType: string
  startTs: number
  endTs: number | undefined
  isAllDay: boolean
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ABSENCE_PLANNING_TYPES = ['VACATION', 'ILLNESS', 'ABSENCE', 'STAND_BY']

const PLANNING_TYPE_LABELS: Record<string, string> = {
  ILLNESS: 'Krank',
  VACATION: 'Urlaub',
  ABSENCE: 'Abwesend',
  STAND_BY: 'Bereitschaft',
  OVERTIME: 'Überstunden',
}

const PLANNING_TYPE_VARIANTS: Record<string, 'danger' | 'warning' | 'neutral' | 'info' | 'success'> = {
  ILLNESS: 'danger',
  VACATION: 'success',
  ABSENCE: 'warning',
  STAND_BY: 'info',
  OVERTIME: 'neutral',
}

const QUICK_FILTER_OPTIONS = [
  { value: 'all', label: 'Alle' },
  { value: 'ILLNESS', label: 'Krank' },
  { value: 'VACATION', label: 'Urlaub' },
  { value: 'ABSENCE', label: 'Abwesend' },
  { value: 'STAND_BY', label: 'Bereitschaft' },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayIso(): string {
  return new Date().toISOString().split('T')[0]!
}

function plusDaysIso(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d.toISOString().split('T')[0]!
}

function formatTs(ts: number | undefined): string {
  if (ts == null) return '–'
  return new Date(ts * 1000).toLocaleDateString('de-DE')
}

function durationLabel(startTs: number, endTs: number | undefined): string {
  if (endTs == null) return '1 Tag'
  const diffMs = (endTs - startTs) * 1000
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  if (days <= 1) return '1 Tag'
  return `${days} Tage`
}

/** Derives a flat absence list from normalized DispoCalendarEmployee[] */
function deriveAbsences(employees: DispoCalendarEmployee[]): AbsenceRow[] {
  return employees.flatMap((emp) =>
    emp.events
      .filter((e: DispoCalendarEvent) => e.isAbsence)
      .map((e: DispoCalendarEvent) => ({
        id: e.id,
        employeeId: emp.id,
        employeeName: emp.name,
        planningType: e.type,
        startTs: e.startTs,
        endTs: e.endTs,
        isAllDay: e.isAllDay,
      }))
  )
}

// ─── Columns ─────────────────────────────────────────────────────────────────

const columns: ColumnDef<AbsenceRow>[] = [
  {
    id: 'employee',
    header: 'Mitarbeiter',
    accessorFn: (r) => r.employeeName,
    cell: ({ row }) => (
      <span className="font-medium text-neutral-900">{row.original.employeeName}</span>
    ),
  },
  {
    id: 'planningType',
    header: 'Typ',
    accessorFn: (r) => r.planningType,
    cell: ({ row }) => {
      const type = row.original.planningType
      return (
        <Badge variant={PLANNING_TYPE_VARIANTS[type] ?? 'neutral'} size="sm">
          {PLANNING_TYPE_LABELS[type] ?? type}
        </Badge>
      )
    },
    size: 120,
  },
  {
    id: 'start',
    header: 'Beginn',
    accessorFn: (r) => r.startTs,
    cell: ({ row }) => (
      <span className="text-sm text-neutral-700">{formatTs(row.original.startTs)}</span>
    ),
    size: 120,
  },
  {
    id: 'end',
    header: 'Ende',
    accessorFn: (r) => r.endTs,
    cell: ({ row }) => (
      <span className="text-sm text-neutral-700">{formatTs(row.original.endTs)}</span>
    ),
    size: 120,
  },
  {
    id: 'duration',
    header: 'Zeitraum',
    accessorFn: (r) => r.startTs,
    cell: ({ row }) => (
      <span className="text-sm text-neutral-500">
        {durationLabel(row.original.startTs, row.original.endTs)}
      </span>
    ),
    size: 100,
  },
]

// ─── Page ─────────────────────────────────────────────────────────────────────

export function DispoAbwesenheitPage() {
  const { toast } = useToast()
  const [from, setFrom] = useState(todayIso)
  const [to, setTo] = useState(() => plusDaysIso(28))
  const [typeFilter, setTypeFilter] = useState<AbsenceTypeFilter>('all')
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null)

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<AbwesenheitResponse>({
    queryKey: ['dispo-abwesenheit', from, to],
    queryFn: async () => {
      const res = await apiClient.get('/dispo/abwesenheit', { params: { from, to } })
      return res.data
    },
    retry: false,
  })

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/dispo/abwesenheit/sync-outlook', {})
      return res.data as SyncResult
    },
    onSuccess: (result) => {
      setSyncResult(result)
      toast({
        title: 'Outlook-Sync abgeschlossen',
        description: `${result.created} erstellt, ${result.updated} aktualisiert, ${result.deleted} gelöscht${result.errors > 0 ? `, ${result.errors} Fehler` : ''}`,
        tone: result.errors > 0 ? 'warning' : 'success',
      })
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        String(err)
      toast({ title: 'Sync fehlgeschlagen', description: msg, tone: 'error' })
    },
  })

  // Derive flat absence rows from the same normalized employee dataset as the calendar.
  // Both calendar and list share this data — no second TANSS fetch.
  const allAbsences = useMemo(
    () => deriveAbsences(data?.employees ?? []),
    [data?.employees],
  )

  // Sorted: by startTs ascending, then by employee name
  const sortedAbsences = useMemo(
    () =>
      [...allAbsences].sort((a, b) => {
        const tsDiff = a.startTs - b.startTs
        if (tsDiff !== 0) return tsDiff
        return a.employeeName.localeCompare(b.employeeName)
      }),
    [allAbsences],
  )

  // Apply type filter (display-only; does not restrict the TANSS call)
  const filteredAbsences = useMemo(
    () =>
      typeFilter === 'all'
        ? sortedAbsences
        : sortedAbsences.filter((a) => a.planningType === typeFilter),
    [sortedAbsences, typeFilter],
  )

  // For the calendar: collect all unique absence types to pass as filter
  const calendarVisibleTypes = useMemo(
    () => (typeFilter === 'all' ? ABSENCE_PLANNING_TYPES : [typeFilter]),
    [typeFilter],
  )

  // Calendar starts at the week containing `from`. key forces re-mount on date change.
  const calendarFromDate = useMemo(() => {
    const d = new Date(from)
    return startOfWeek(d, { weekStartsOn: 1 })
  }, [from])

  // Determine error state
  const hasTanssError = isError || data?.dataSource === 'none'
  const tanssErrorMsg = isError
    ? (error instanceof Error ? error.message : 'Unbekannter Fehler')
    : data?.error ?? 'Abwesenheitsdaten konnten nicht geladen werden.'

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <CalendarX2 className="w-6 h-6 text-brand-600" />
            Abwesenheitsübersicht
          </span>
        }
        description="Alle Mitarbeiter-Abwesenheiten im gewählten Zeitraum"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSyncResult(null); void refetch() }}
              disabled={isFetching}
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
            >
              <CloudCog className="w-4 h-4 mr-1.5" />
              {syncMutation.isPending ? 'Sync läuft…' : 'Outlook-Sync'}
            </Button>
          </div>
        }
      />

      {/* Date range */}
      <Card>
        <Card.Body>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-36">
              <Input
                label="Von"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="flex-1 min-w-36">
              <Input
                label="Bis"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Sync result */}
      {syncResult && (
        <Card>
          <Card.Body>
            <div className="flex items-start gap-3">
              <CloudCog className="w-5 h-5 text-brand-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-neutral-900">Outlook-Sync Ergebnis</p>
                <div className="flex gap-4 text-sm text-neutral-700">
                  <span><span className="font-medium text-green-700">{syncResult.created}</span> erstellt</span>
                  <span><span className="font-medium text-blue-700">{syncResult.updated}</span> aktualisiert</span>
                  <span><span className="font-medium text-neutral-500">{syncResult.deleted}</span> gelöscht</span>
                  {syncResult.errors > 0 && (
                    <span><span className="font-medium text-red-700">{syncResult.errors}</span> Fehler</span>
                  )}
                </div>
                {syncResult.errorMessages.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-red-600 cursor-pointer">Fehler anzeigen</summary>
                    <ul className="mt-1 space-y-0.5">
                      {syncResult.errorMessages.map((msg, i) => (
                        <li key={i} className="text-xs text-red-600">{msg}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Filter */}
      <FilterBar>
        {QUICK_FILTER_OPTIONS.map((opt) => (
          <FilterChip
            key={opt.value}
            active={typeFilter === opt.value}
            onClick={() => setTypeFilter(opt.value)}
            onRemove={typeFilter === opt.value && opt.value !== 'all' ? () => setTypeFilter('all') : undefined}
          >
            {opt.label}
          </FilterChip>
        ))}
      </FilterBar>

      {/* Error state */}
      {hasTanssError && (
        <Card>
          <Card.Body>
            <ErrorState
              title="Abwesenheitsdaten nicht verfügbar"
              description={tanssErrorMsg}
            />
          </Card.Body>
        </Card>
      )}

      {/* Shared calendar — uses the same employees data as the list (no second TANSS call) */}
      {!hasTanssError && (
        <Card>
          <Card.Header>
            <span className="text-sm font-medium text-neutral-700">Kalender</span>
          </Card.Header>
          <Card.Body>
            <DispositionCalendar
              key={calendarFromDate.toISOString()}
              from={calendarFromDate}
              showFilter={false}
              showEmployeeImages={false}
              visiblePlanningTypes={calendarVisibleTypes}
              externalData={{
                employees: data?.employees ?? [],
                isLoading,
                error: isError ? new Error(tanssErrorMsg) : null,
              }}
            />
          </Card.Body>
        </Card>
      )}

      {/* Absence list */}
      {!hasTanssError && (
        <>
          {isLoading && (
            <Card>
              <Card.Body>
                <LoadingState label="Abwesenheiten werden geladen…" />
              </Card.Body>
            </Card>
          )}

          {!isLoading && filteredAbsences.length === 0 && (
            <Card>
              <Card.Body>
                <EmptyState
                  icon={<CalendarX2 className="w-8 h-8" />}
                  title="Keine Abwesenheiten im gewählten Zeitraum"
                  description={
                    typeFilter !== 'all'
                      ? `Keine ${PLANNING_TYPE_LABELS[typeFilter] ?? typeFilter}-Einträge im gewählten Zeitraum.`
                      : 'Im gewählten Zeitraum sind keine Abwesenheiten eingetragen.'
                  }
                />
              </Card.Body>
            </Card>
          )}

          {!isLoading && filteredAbsences.length > 0 && (
            <Card>
              <Card.Header>
                <span className="text-sm font-medium text-neutral-700">
                  {filteredAbsences.length} {filteredAbsences.length === 1 ? 'Eintrag' : 'Einträge'}
                </span>
              </Card.Header>
              <Card.Body className="p-0">
                <DataTable columns={columns} data={filteredAbsences} pageSize={25} />
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
